/**
 * Pure helpers for classifying an upload failure. Kept free of store/network
 * imports so the standalone test runner can exercise them.
 */

/**
 * Stable failure buckets. Consumers match on these for logic; the `reason`
 * string that accompanies them is for display only.
 *
 * - `credits` / `storage` — deterministic billing refusals from the backend.
 * - `connection` — the transfer died on the user's link (stall, timeout,
 *   network error, offline). With the connection-scoped stall detector this
 *   is only reached after the chunk layer's full retry budget, so blaming the
 *   connection is accurate rather than a guess.
 * - `other` — anything else (server 5xx, expired URL, unexpected error).
 */
export const UPLOAD_FAILURE_CATEGORIES = ['credits', 'storage', 'connection', 'other'] as const;
export type UploadFailureCategory = (typeof UPLOAD_FAILURE_CATEGORIES)[number];

/** User-facing reason for any connectivity-shaped failure (handshake timeout,
 *  chunk stall/abort, offline fetch). Keeps dev strings like "Upload request
 *  timed out after 30000ms" / "Failed to upload chunk 4" out of the UI. */
export const CONNECTION_FAILURE_REASON = 'Unstable connection — check your internet and retry';

/** Matches the genuine connectivity/timeout messages our upload layer produces
 *  (stall, XHR network error, request timeout, offline fetch). Deliberately does
 *  NOT match the `Failed to upload chunk N` wrapper — a chunk can also exhaust its
 *  retries on a server-side cause (expired presigned URL → 403, S3 500), which is
 *  not the user's connection; those are handled as a neutral 'other' failure in
 *  describeUploadFailure so telemetry doesn't misattribute them. */
const CONNECTIVITY_MESSAGE =
  /timed out|stalled for|network error uploading|failed to fetch|networkerror when attempting to fetch|the network connection was lost/i;

/**
 * True for a failure caused by the connection (timeout / stall / offline), so it
 * can be shown as a friendly reason. Checks the error's own message and its
 * `cause` — the chunk-failure wrapper carries the real message as `cause`.
 */
export function isConnectivityFailure(error: unknown): boolean {
  // Per-attempt request timeout (signalWithTimeout → AbortSignal.timeout) surfaces
  // as a TimeoutError; match by name since the message is browser-defined.
  if (error instanceof DOMException && error.name === 'TimeoutError') return true;
  if (error instanceof Error || error instanceof DOMException) {
    const cause = (error as { cause?: unknown }).cause;
    const causeText =
      typeof cause === 'string' ? cause : cause instanceof Error ? cause.message : '';
    return CONNECTIVITY_MESSAGE.test(`${error.message} ${causeText}`);
  }
  return CONNECTIVITY_MESSAGE.test(String(error ?? ''));
}
